/******************************************************************************
 * FILE:
 * src/modules/expenseManagement/workflow/hooks/useWorkflowMaster.js
 *
 * MODULE:
 * Expense Management
 *
 * FEATURE:
 * Workflow Master
 *
 * PURPOSE:
 * React Hook for Workflow Master Management
 *
 * RESPONSIBILITY:
 * • Load Workflows
 * • Load Workflow Details
 * • Create Workflow
 * • Update Workflow
 * • Delete Workflow
 * • Activate Workflow
 * • Deactivate Workflow
 * • Refresh
 *
 * ARCHITECTURE:
 * • No UI
 * • No JSX
 * • Uses workflowMasterService
 ******************************************************************************/

import {
  useCallback,
  useState,
} from "react";

import workflowMasterService
from "../services/workflowMasterService";

import expenseMasterService
from "../../claims/services/expenseMasterService";

export default function useWorkflowMaster() {

  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState(null);

  const [workflows, setWorkflows] =
    useState([]);

  const [workflow, setWorkflow] =
    useState(null);

  const [workflowTypes, setWorkflowTypes] =
    useState([]);

const [departments, setDepartments] =
    useState([]);  
  /**
   * ------------------------------------------------------------------------
   * LOAD WORKFLOWS
   * ------------------------------------------------------------------------
   */
  const loadWorkflows =
    useCallback(
      async (params = {}) => {

        try {

          setLoading(true);

          setError(null);

          const data =
            await workflowMasterService
              .getWorkflows(params);

          setWorkflows(data);

          return data;

        } catch (err) {

          const message =
            err?.response?.data?.message
            || err.message
            || "Failed to load workflows.";

          setError(message);

          throw err;

        } finally {

          setLoading(false);
        }

      },
      []
    );
  
  /**
 * ------------------------------------------------------------------------
 * LOAD WORKFLOW TYPES
 * ------------------------------------------------------------------------
 */
const loadWorkflowTypes =
    useCallback(
        async () => {

            try {

                const data =
                    await expenseMasterService
                        .listWorkflowTypes();

                setWorkflowTypes(data);

                return data;

            } catch (err) {

                const message =
                    err?.response?.data?.message
                    || err.message
                    || "Failed to load workflow types.";

                setError(message);

                throw err;

            }

        },
        []
    );

/**
 * ------------------------------------------------------------------------
 * LOAD DEPARTMENTS
 * ------------------------------------------------------------------------
 */
const loadDepartments =
    useCallback(
        async () => {

            try {

                const data =
                    await expenseMasterService
                        .listDepartments();

                setDepartments(data);

                return data;

            } catch (err) {

                const message =
                    err?.response?.data?.message
                    || err.message
                    || "Failed to load departments.";

                setError(message);

                throw err;

            }

        },
        []
    );  
  /**
   * ------------------------------------------------------------------------
   * LOAD WORKFLOW
   * ------------------------------------------------------------------------
   */
  const loadWorkflow =
    useCallback(
      async (workflowId) => {

        try {

          setLoading(true);

          setError(null);

          const data =
            await workflowMasterService
              .getWorkflow(workflowId);

          setWorkflow(data);

          return data;

        } catch (err) {

          const message =
            err?.response?.data?.message
            || err.message
            || "Failed to load workflow.";

          setError(message);

          throw err;

        } finally {

          setLoading(false);
        }

      },
      []
    );

  /**
   * ------------------------------------------------------------------------
   * CREATE
   * ------------------------------------------------------------------------
   */
  const createWorkflow =
    useCallback(
      async (payload) => {

        try {

          setSaving(true);

          setError(null);

          return await workflowMasterService
            .createWorkflow(payload);

        } catch (err) {

          const message =
            err?.response?.data?.message
            || err.message
            || "Failed to create workflow.";

          setError(message);

          throw err;

        } finally {

          setSaving(false);
        }

      },
      []
    );

  /**
   * ------------------------------------------------------------------------
   * UPDATE
   * ------------------------------------------------------------------------
   */
  const updateWorkflow =
    useCallback(
      async (
        workflowId,
        payload
      ) => {

        try {

          setSaving(true);

          setError(null);

          return await workflowMasterService
            .updateWorkflow(
              workflowId,
              payload
            );

        } catch (err) {

          const message =
            err?.response?.data?.message
            || err.message
            || "Failed to update workflow.";

          setError(message);

          throw err;

        } finally {

          setSaving(false);
        }

      },
      []
    );

  /**
   * ------------------------------------------------------------------------
   * DELETE
   * ------------------------------------------------------------------------
   */
  const deleteWorkflow =
    useCallback(
      async (workflowId) => {

        try {

          setSaving(true);

          setError(null);

          return await workflowMasterService
            .deleteWorkflow(workflowId);

        } catch (err) {

          const message =
            err?.response?.data?.message
            || err.message
            || "Failed to delete workflow.";

          setError(message);

          throw err;

        } finally {

          setSaving(false);
        }

      },
      []
    );

  /**
   * ------------------------------------------------------------------------
   * ACTIVATE
   * ------------------------------------------------------------------------
   */
  const activateWorkflow =
    useCallback(
      async (workflowId) => {

        try {

          setSaving(true);

          setError(null);

          return await workflowMasterService
            .activateWorkflow(workflowId);

        } catch (err) {

          const message =
            err?.response?.data?.message
            || err.message
            || "Failed to activate workflow.";

          setError(message);

          throw err;

        } finally {

          setSaving(false);
        }

      },
      []
    );

  /**
   * ------------------------------------------------------------------------
   * DEACTIVATE
   * ------------------------------------------------------------------------
   */
  const deactivateWorkflow =
    useCallback(
      async (workflowId) => {

        try {

          setSaving(true);

          setError(null);

          return await workflowMasterService
            .deactivateWorkflow(workflowId);

        } catch (err) {

          const message =
            err?.response?.data?.message
            || err.message
            || "Failed to deactivate workflow.";

          setError(message);

          throw err;

        } finally {

          setSaving(false);
        }

      },
      []
    );

  /**
   * ------------------------------------------------------------------------
   * RETURN
   * ------------------------------------------------------------------------
   */
  return {

    loading,

    saving,

    error,

    workflows,

    workflow,

    workflowTypes,

    departments,

    loadWorkflows,

    loadWorkflow,

    loadWorkflowTypes,

    loadDepartments,

    createWorkflow,

    updateWorkflow,

    deleteWorkflow,

    activateWorkflow,

    deactivateWorkflow,

};
}